import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../redux'
import libertyLakeSlice from '../../../redux/slice/libertyLake.slice'
import appSlice from '../../../redux/slice/app.slice'
import axios from 'axios'
import { config as appConfig } from '../../../config'

const Footer = () => {
  const dispatch = useDispatch()
  const { step, kids, familyInfo, config } = useSelector((state: RootState) => state.libertyLake)

  const MIN_WEEKS_PER_CAMPER = 2
  const [footerErrorMsg, setFooterErrorMsg] = useState('')

  const returnToWebsite = () => {
    window.location.assign('https://libertylakedaycamp.com/about-us-2/')
  }

  const returnToEnrollmentForm = () => {
    window.location.assign('https://libertylake.campintouch.com/ui/forms/application/camper/App')
  }

  const computedPricing = useMemo(() => {
    if (!config) return { subtotal: 0, totalDiscount: 0, grandTotal: 0 }
    const { basePricePerWeek, aMBusExtendedCare, pMBusExtendedCare } = config.pricing
    const { multiWeek, preSchool, siblingNumber, occupational, newFamily, returningFamily, earlyPay } = config.discounts
    const preSchoolPct = preSchool['Pre-K, Kindergarten'] || 0.05
    const familyFlatDiscountPerKid = familyInfo.isNewFamily
      ? (newFamily['per Kid'] || 0)
      : (returningFamily['per Kid'] || 0)

    const kidBreakdowns = kids.map((kid) => {
      const weeklyRate = basePricePerWeek + (kid.includeAMBus ? aMBusExtendedCare : 0) + (kid.includePMBus ? pMBusExtendedCare : 0)
      const gross = weeklyRate * kid.weeksTotal
      const multiWeekDiscount = (multiWeek[kid.weeksTotal.toString()] || 0) * kid.weeksTotal
      const priceAfterMultiWeek = gross - multiWeekDiscount
      const priceAfterFamilyFlat = Math.max(0, priceAfterMultiWeek - familyFlatDiscountPerKid)
      const priceAfterPreSchool = kid.grade === 'Pre thru K' ? priceAfterFamilyFlat * (1 - preSchoolPct) : priceAfterFamilyFlat
      return { gross, priceAfterPreSchool }
    })

    const subtotal = kidBreakdowns.reduce((sum, b) => sum + b.gross, 0)
    const sorted = [...kidBreakdowns].sort((a, b) => b.priceAfterPreSchool - a.priceAfterPreSchool)
    let familyTotal = 0
    sorted.forEach((b, i) => {
      const sibPct = kids.length < 2 ? 0 : (siblingNumber[(i + 1).toString()] || 0)
      familyTotal += b.priceAfterPreSchool * (1 - sibPct)
    })

    const occPct = occupational[familyInfo.occupation] || 0
    const priceAfterOcc = familyTotal * (1 - occPct)
    const earlyPayPct = familyInfo.earlyPayOption ? (earlyPay[familyInfo.earlyPayOption] || 0) : 0
    const grandTotal = Math.max(0, priceAfterOcc * (1 - earlyPayPct))
    return { subtotal, totalDiscount: subtotal - grandTotal, grandTotal }
  }, [kids, config, familyInfo])

  const isStepOneValid = (): boolean => {
    return (
      kids.length > 0 &&
      familyInfo.firstName.length > 0 &&
      familyInfo.lastName.length > 0 &&
      familyInfo.email.length > 0 &&
      familyInfo.city.length > 0
    )
  }

  const isStepTwoValid = (): boolean => {
    return kids.every(k => k.grade.length > 0 && k.weeksTotal >= MIN_WEEKS_PER_CAMPER)
  }

  const isStepThreeValid = (): boolean => {
    return familyInfo.occupation.length > 0
  }

  const isStepValid = () => {
    if (step === 1) return isStepOneValid()
    if (step === 2) return isStepTwoValid()
    if (step === 3) return isStepThreeValid()
    if (step === 4) return true
    return false
  }

  const step2ValidationMessage = useMemo(() => {
    if (step !== 2) return ''
    if (kids.some(k => !k.grade?.length)) return 'Please select a grade for each camper.'
    if (kids.some(k => (k.weeksTotal || 0) < MIN_WEEKS_PER_CAMPER)) return 'Camper must be registered for at least 2 weeks.'
    return ''
  }, [MIN_WEEKS_PER_CAMPER, kids, step])

  useEffect(() => {
    if (step !== 2) {
      setFooterErrorMsg('')
      return
    }
    if (footerErrorMsg && isStepTwoValid()) {
      setFooterErrorMsg('')
    }
  }, [footerErrorMsg, step, kids])

  const nextClickHandler = () => {
    if (step === 1 && isStepOneValid()) {
      dispatch(libertyLakeSlice.actions.setStepNumber(step + 1))
    } else if (step === 2) {
      if (!isStepTwoValid()) {
        setFooterErrorMsg(step2ValidationMessage || 'Camper must be registered for at least 2 weeks.')
        return
      }
      setFooterErrorMsg('')
      dispatch(libertyLakeSlice.actions.setStepNumber(step + 1))
    } else if (step === 3 && isStepThreeValid()) {
      const camperAndSessions = kids.map(k => ({
        allSessions: [String(k.weeksTotal)], // since allSessions is an array of strings, we convert weeksTotal to string
        grade: k.grade,
        gender: '' // gender is not captured in the current form, so we leave it empty
      }))
      const data = {
        version: 'v2',
        firstName: familyInfo.firstName,
        lastName: familyInfo.lastName,
        email: familyInfo.email,
        city: familyInfo.city,
        phoneNumber: familyInfo.phone ?? '',
        numberOfCampers: kids.length,
        camperAndSessions,
        subTotal: computedPricing.subtotal,
        discounts: computedPricing.totalDiscount,
        addonsCost: 0,
        grandTotal: computedPricing.grandTotal
      }
      dispatch(appSlice.actions.setLoading(true))
      axios({
        method: 'POST',
        url: appConfig.libertyLakeWorkflowURL,
        headers: { 'Cache-Control': 'no-cache' },
        data
      })
        .then(res => console.log(res))
        .catch(err => console.log(err))
      dispatch(appSlice.actions.setLoading(false))
      dispatch(libertyLakeSlice.actions.setStepNumber(step + 1))
    }
  }

  return <div className='footerContainer'>
    <div className="buttonContainer">
      {step === 2 && footerErrorMsg.length > 0 && (
        <div className='footerValidationText'>
          {footerErrorMsg}
        </div>
      )}
      {step > 1 && (
        <button
          className="back"
          onClick={() => dispatch(libertyLakeSlice.actions.setStepNumber(step - 1))}
        >
          Previous
        </button>
      )}
      {step === 4 && (
        <>
          <button
            type="button"
            className="returnToWeb"
            onClick={returnToWebsite}
          >
            Return to website
          </button>
          <button
            type="button"
            className="enrollNow"
            onClick={returnToEnrollmentForm}
          >
            Enroll Now
          </button>
        </>
      )}
      <button
        className={`next ${step >= 4 && 'hide'}`}
        disabled={(step !== 2 && !isStepValid()) || step === 4}
        onClick={() => nextClickHandler()}
      >
        Next
      </button>
    </div>
  </div>
}

export default Footer
